import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Loader2, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import Footer from "@/components/Footer";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);

  if (user && typeof user === "object") return <Navigate to="/" replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await login(email.trim().toLowerCase(), password);
    setLoading(false);
    if (res.ok) {
      toast.success("Berhasil masuk");
      navigate("/", { replace: true });
    } else {
      toast.error(res.error || "Gagal masuk");
    }
  };

  return (
    <div className="min-h-screen flex font-body bg-slate-50">
      {/* Left visual panel */}
      <div className="hidden lg:flex w-1/2 relative overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url(https://images.pexels.com/photos/36232116/pexels-photo-36232116.jpeg)",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-900/90 via-emerald-800/85 to-emerald-700/80" />
        <div className="relative z-10 flex flex-col justify-between p-12 text-white w-full">
          <div className="flex items-center gap-4">
            <div className="h-16 w-16 rounded-2xl bg-white p-1.5 shadow-md flex items-center justify-center">
              <img
                src="/assets/logo.png"
                alt="Logo Pondok Pesantren Sunan Giri"
                className="h-full w-full object-contain"
                data-testid="login-visual-logo"
              />
            </div>
            <div>
              <div className="font-heading text-2xl font-bold">SiMadin</div>
              <div className="text-xs text-emerald-100/80 uppercase tracking-widest">
                Sunan Giri Surabaya
              </div>
            </div>
          </div>
          <div>
            <h1 className="font-heading text-4xl xl:text-5xl font-bold leading-tight">
              Sistem Informasi<br />Madrasah Al Islamiyyah
            </h1>
            <p className="mt-4 text-emerald-100/90 max-w-md leading-relaxed">
              Kelola data santri, kelas, dan ustadz dengan rapi.
              Fondasi administrasi modern untuk Madrasah Diniyah.
            </p>
            <div className="mt-8 flex items-center gap-3 text-emerald-100/70 text-sm">
              <div className="h-1 w-12 bg-emerald-300 rounded-full" />
              <span>Versi 1.0 — Fondasi</span>
            </div>
          </div>
          <div className="text-xs text-emerald-100/60">
            &copy; {new Date().getFullYear()} Madrasah Al Islamiyyah Sunan Giri
          </div>
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex flex-col items-center justify-center px-4 py-12">
        <Card className="w-full max-w-md p-8 border border-slate-200 shadow-sm bg-white">
          <div className="lg:hidden flex items-center gap-3 mb-6">
            <img
              src="/assets/logo.png"
              alt="Logo Pondok"
              className="h-12 w-12 object-contain"
              data-testid="login-form-logo"
            />
            <div>
              <div className="font-heading font-bold text-slate-900 text-lg">SiMadin</div>
              <div className="text-[10px] text-slate-500 uppercase tracking-wider">Sunan Giri Surabaya</div>
            </div>
          </div>

          <h2 className="font-heading text-2xl font-bold text-slate-900">Selamat Datang</h2>
          <p className="text-sm text-slate-500 mt-1">Masuk untuk mengelola data Madrasah.</p>

          <form onSubmit={handleSubmit} className="mt-7 space-y-4">
            <div>
              <Label htmlFor="email" className="text-sm font-medium text-slate-700">Email</Label>
              <Input
                id="email"
                type="email"
                required
                placeholder="Masukkan email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email-input"
                className="mt-1.5 h-11 focus-visible:ring-emerald-500/30 focus-visible:border-emerald-500"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-sm font-medium text-slate-700">Kata Sandi</Label>
              <div className="relative mt-1.5">
                <Input
                  id="password"
                  type={showPwd ? "text" : "password"}
                  required
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="login-password-input"
                  className="h-11 pr-10 focus-visible:ring-emerald-500/30 focus-visible:border-emerald-500"
                />
                <button
                  type="button"
                  onClick={() => setShowPwd((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  tabIndex={-1}
                  data-testid="login-password-toggle"
                >
                  {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="w-full h-11 bg-emerald-600 hover:bg-emerald-700 text-white font-medium shadow-sm"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Masuk"}
            </Button>
          </form>

          <div className="mt-6 text-xs text-slate-400 text-center">
            Aplikasi internal untuk staf Madrasah.
          </div>
        </Card>
        <Footer className="mt-6" />
      </div>
    </div>
  );
}
